import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Analytics,
  TrendingUp,
  Group,
  LocalHospital,
  Calculate,
  Timeline,
  HealthAndSafety,
  Psychology,
} from "@mui/icons-material";
import { DimensionamentoService } from "../../services/DimensionamentoServices";

const AverageProviderReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reportConfigurations = {
    utilizacao_operadora: {
      title: "Dados de Utilização da Operadora",
      icon: <Analytics className="h-6 w-6" />,
      items: [
        {
          label: "Total de beneficiários da Operadora",
          field: "totalBeneficiaries",
          format: "number",
        },
        {
          label: "Nº de consultas médicas/ano",
          field: "totalConsultasAno",
          format: "number",
        },
        {
          label: "Média de consultas médicas/beneficiário/ano",
          field: "meanConsultasPerBeneficiary",
          format: "decimal",
        },
        {
          label: "Nº consultas esperadas/ano",
          field: "totalConsultasEsperadasAno",
          format: "number",
        },
      ],
    },
    exames_ambulatoriais: {
      title: "Dados de Exames e Procedimentos",
      icon: <Timeline className="h-6 w-6" />,
      items: [
        {
          label: "Nº de EXAMES ambulatoriais/ano",
          field: "totalExamesAmbulatoriaisAno",
          format: "number",
        },
        {
          label: "Taxa de exames ambulatoriais/consulta/ano",
          field: "taxExamesAmbulatoriaisPerConsulta",
          format: "decimal",
        },
        {
          label: "Taxa de exames/beneficiário/ano",
          field: "taxExamesAmbulatoriaisPerBeneficiary",
          format: "decimal",
        },
      ],
    },
    procedimentos_especializados: {
      title: "Procedimentos Especializados",
      icon: <Psychology className="h-6 w-6" />,
      items: [
        {
          label: "Consultas outros profissionais/ano",
          field: "totalConsultasOutrosProfissionaisAno",
          format: "number",
        },
        {
          label: "Consultas fisioterapia/ano",
          field: "totalConsultasFisioterapiaAno",
          format: "number",
        },
        {
          label: "Média consultas fisioterapia/beneficiário/ano",
          field: "meanConsultasFisioterapiaPerBeneficiary",
          format: "decimal",
        },
        {
          label: "Consultas psicologia/ano",
          field: "totalConsultasPsicologiaAno",
          format: "number",
        },
        {
          label: "Média consultas psicologia/beneficiário/ano",
          field: "meanConsultasPsicologiaPerBeneficiary",
          format: "decimal",
        },
        {
          label: "Consultas nutrição/ano",
          field: "totalConsultasNutricionistaAno",
          format: "number",
        },
        {
          label: "Média consultas nutrição/beneficiário/ano",
          field: "meanConsultasNutricionistaPerBeneficiary",
          format: "decimal",
        },
        {
          label: "Consultas fonoaudiologia/ano",
          field: "totalConsultasFonoaudiologiaAno",
          format: "number",
        },
        {
          label: "Média consultas fonoaudiologia/beneficiário/ano",
          field: "meanConsultasFonoaudiologiaPerBeneficiary",
          format: "decimal",
        },
        {
          label: "Consultas não médicos/ano",
          field: "totalConsultasNaoMedicosAno",
          format: "number",
        },
        {
          label: "Média consultas não médicos/beneficiário/ano",
          field: "meanConsultasNaoMedicosPerBeneficiary",
          format: "decimal",
        },
      ],
    },
    internacoes_procedimentos: {
      title: "Internações e Procedimentos Hospitalares",
      icon: <HealthAndSafety className="h-6 w-6" />,
      items: [
        {
          label: "Internações hospitalares/ano",
          field: "totalInternacoesHospitalaresAno",
          format: "number",
        },
        {
          label: "Percentual internações hospitalares/beneficiário/ano",
          field: "percentegeInternacoesHospitalaresPerBeneficiary",
          format: "percent",
        },
        {
          label: "Procedimentos ambulatoriais/ano",
          field: "totalProcedimentosAmbulatoriaisAno",
          format: "number",
        },
        {
          label: "Média procedimentos ambulatoriais/beneficiário/ano",
          field: "meanProcedimentosAmbulatoriaisPerBeneficiary",
          format: "decimal",
        },
        {
          label: "Internações clínicas/ano",
          field: "totalInternacoesClinicasAno",
          format: "number",
        },
        {
          label: "Percentual internações clínicas/beneficiário/ano",
          field: "percentegeInternacoesClinicasPerBeneficiary",
          format: "percent",
        },
        {
          label: "Internações cirúrgicas/ano",
          field: "totalInternacoesCirurgicasAno",
          format: "number",
        },
        {
          label: "Percentual internações cirúrgicas/beneficiário/ano",
          field: "percentegeInternacoesCirurgicasPerBeneficiary",
          format: "percent",
        },
        {
          label: "Internações UTI/ano",
          field: "totalInternacoesUTIAno",
          format: "number",
        },
        {
          label: "Partos/ano",
          field: "totalPartosAno",
          format: "number",
        },
        {
          label: "Partos esperados",
          field: "totalPartosEsperados",
          format: "number",
        },
        {
          label: "Nascidos vivos anterior",
          field: "totalNascidosVivosAnoAnterior",
          format: "number",
        },
        {
          label: "Nascidos vivos esperados",
          field: "totalNascidosVivosEsperado",
          format: "number",
        },
      ],
    },
  };

  const formatValue = (value, format) => {
    const numValue = Number(value);

    switch (format) {
      case "number":
        return new Intl.NumberFormat("pt-BR").format(Math.round(numValue));
      case "decimal":
        return new Intl.NumberFormat("pt-BR", {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        }).format(numValue);
      case "percent":
        return new Intl.NumberFormat("pt-BR", {
          style: "percent",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);
      default:
        return numValue.toString();
    }
  };

  const renderDynamicTables = () => {
    if (!reportData) return null;

    return Object.entries(reportConfigurations).map(([configKey, config]) => (
      <Card
        key={configKey}
        elevation={2}
        className="border border-gray-200 mb-6 flex flex-col"
        sx={{
          height: "400px",
          overflow: "hidden",
          "&.MuiCard-root": {
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-b border-emerald-200 p-4 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <div className="text-emerald-700">{config.icon}</div>
            </div>
            <Typography variant="h6" className="font-semibold text-gray-800">
              {config.title}
            </Typography>
          </div>
        </div>

        <CardContent className="p-0 flex-grow overflow-auto">
          <TableContainer sx={{ height: "100%", overflow: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow className="bg-gray-50">
                  <TableCell className="font-semibold text-gray-700 border-r border-gray-200">
                    Indicador
                  </TableCell>
                  <TableCell className="font-semibold text-gray-700 text-center">
                    Resultado
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {config.items.map((item, index) => {
                  const value = reportData[item.field] || 0;
                  const formattedValue = formatValue(value, item.format);

                  return (
                    <TableRow
                      key={index}
                      className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}
                    >
                      <TableCell className="border-r border-gray-200 font-medium text-gray-700">
                        {item.label}
                      </TableCell>
                      <TableCell
                        className={`text-center font-semibold ${
                          value > 0
                            ? "bg-green-100 text-green-800"
                            : "text-gray-600"
                        }`}
                      >
                        {formattedValue}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    ));
  };

  useEffect(() => {
    const fetchData = async () => {
      const requiredFields = [
        "totalBeneficiaries",
        "totalConsultasAno",
        "meanConsultasPerBeneficiary",
      ];
      try {
        setLoading(true);
        setError(null);
        setReportData(null);

        const data = await DimensionamentoService.getNetworkUsageReport();

        const isEmpty =
          !data ||
          (Array.isArray(data) && data.length === 0) ||
          (typeof data === "object" && Object.keys(data).length === 0);

        const hasRequiredFields = requiredFields.every(
          (field) => data && data[field] !== undefined && data[field] !== null
        );

        if (isEmpty || !hasRequiredFields) {
          throw new Error("Dados recebidos são inválidos ou incompletos");
        }

        setReportData(data);
      } catch (err) {
        setError(err.message || "Erro ao carregar dados de utilização");
        console.error("Erro detalhado:", err);

        if (err.response) {
          console.error("Resposta da API:", err.response);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const renderContent = () => {
    if (error) {
      return (
        <Card elevation={2} className="border border-gray-200">
          <CardContent className="text-center py-12">
            <Alert severity="error" className="mb-4 items-center">
              <Typography variant="h6">{error}</Typography>
            </Alert>
            <Typography variant="body2" className="text-gray-500">
              Tente recarregar a página ou verificar sua conexão
            </Typography>
          </CardContent>
        </Card>
      );
    }

    if (!reportData) {
      return (
        <Card elevation={2} className="border border-gray-200">
          <CardContent className="text-center py-12">
            <Calculate className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <Typography variant="h6" className="text-gray-600 mb-2">
              Nenhum dado encontrado
            </Typography>
            <Typography variant="body2" className="text-gray-500">
              Não há dados de utilização disponíveis no momento
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return renderDynamicTables();
  };

  return (
    <Container maxWidth="xl" className="py-8">
      <Paper
        elevation={0}
        className="mb-8 p-6 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl"
      >
        <div className="flex items-center space-x-4 mb-4">
          <div className="p-3 bg-emerald-100 rounded-xl">
            <Calculate className="h-8 w-8 text-emerald-700" />
          </div>
          <div>
            <Typography variant="h4" className="font-bold text-gray-800">
              Indicadores de{" "}
              <span className="text-emerald-700">Utilização da Operadora</span>
            </Typography>
            <Typography variant="body1" className="text-gray-600 mt-1">
              Análise dinâmica de médias e indicadores de utilização
            </Typography>
          </div>
        </div>

        {reportData && !loading && (
          <Grid container spacing={3} className="mt-4">
            <Grid item xs={12} sm={6} xl={3}>
              <div className="bg-white rounded-lg p-4 border border-emerald-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <Typography
                      variant="h6"
                      className="font-semibold text-emerald-700"
                    >
                      {Object.keys(reportConfigurations).length}
                    </Typography>
                    <Typography variant="body2" className="text-gray-600">
                      Categorias
                    </Typography>
                  </div>
                  <Analytics className="h-8 w-8 text-emerald-500" />
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <div className="bg-white rounded-lg p-4 border border-emerald-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <Typography
                      variant="h6"
                      className="font-semibold text-emerald-700"
                    >
                      {reportData.totalBeneficiaries
                        ? new Intl.NumberFormat("pt-BR").format(
                            reportData.totalBeneficiaries
                          )
                        : "0"}
                    </Typography>
                    <Typography variant="body2" className="text-gray-600">
                      Beneficiários
                    </Typography>
                  </div>
                  <Group className="h-8 w-8 text-emerald-500" />
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <div className="bg-white rounded-lg p-4 border border-emerald-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <Typography
                      variant="h6"
                      className="font-semibold text-emerald-700"
                    >
                      {reportData.totalConsultasAno
                        ? new Intl.NumberFormat("pt-BR").format(
                            reportData.totalConsultasAno
                          )
                        : "0"}
                    </Typography>
                    <Typography variant="body2" className="text-gray-600">
                      Consultas/Ano
                    </Typography>
                  </div>
                  <LocalHospital className="h-8 w-8 text-emerald-500" />
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <div className="bg-white rounded-lg p-4 border border-emerald-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <Typography
                      variant="h6"
                      className="font-semibold text-emerald-700"
                    >
                      {reportData.meanConsultasPerBeneficiary
                        ? reportData.meanConsultasPerBeneficiary.toFixed(3)
                        : "0.000"}
                    </Typography>
                    <Typography variant="body2" className="text-gray-600">
                      Média/Beneficiário
                    </Typography>
                  </div>
                  <TrendingUp className="h-8 w-8 text-emerald-500" />
                </div>
              </div>
            </Grid>
          </Grid>
        )}
      </Paper>

      {loading ? (
        <Card elevation={2} className="border border-gray-200">
          <CardContent className="p-12">
            <div className="space-y-8">
              <div className="text-center">
                <Skeleton
                  variant="circular"
                  width={80}
                  height={80}
                  className="mx-auto mb-4"
                />
                <Skeleton
                  variant="text"
                  height={40}
                  width="60%"
                  className="mx-auto mb-2"
                />
                <Skeleton
                  variant="text"
                  height={24}
                  width="40%"
                  className="mx-auto"
                />
              </div>

              <div className="space-y-4">
                {[1, 2, 3].map((item) => (
                  <div key={item} className="bg-gray-50 rounded-lg p-6">
                    <Skeleton variant="rectangular" height={200} />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {reportData ? (
            renderDynamicTables()
          ) : (
            <div className="space-y-6">{renderContent()}</div>
          )}
        </div>
      )}
    </Container>
  );
};

export default AverageProviderReport;
